// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useId, type Ref } from 'react';

import { BLOCK_REASON, blockingCountText, blockingDetail, type BlockingItem } from '../../../bygning/blocking';
import { Pill } from '../../Pill';
import { TextLink } from '../../TextLink';
import styles from './RapportList.module.css';

export interface BlockingListProps {
  items: readonly BlockingItem[];
  /** Opens the type (Gennemgang: in its inspector). */
  onOpenType: (typeId: number) => void;
  /** The heading's id, for a link that jumps here (focused, so `tabIndex` -1); generated when left out. */
  id?: string;
  headingRef?: Ref<HTMLHeadingElement>;
  /** Show only the first `cap` rows, then "Vis alle N"; undefined = every row. */
  cap?: number;
  /** Every row shown despite `cap` (the host opens it when it jumps here). */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** The type the host shows: its row is marked (`aria-pressed`). Undefined = rows are plain links. */
  activeTypeId?: number | null;
  /** On a phone the list is one sideways-scrolling row (the left tile as a bar). */
  phoneBar?: boolean;
  /** The fractions could not be read: the list is the types' own reading. */
  failed?: boolean;
  onRetry?: () => void;
}

/**
 * "Det, der blokerer" (prototype Rapport tab; now Gennemgang's left tile):
 * the types that keep a new version a kladde, each with every reason as a
 * pill, each opening the type — all of them, or the first `cap` and then
 * "Vis alle N". A type the server names but the case does not hold (yet) is
 * listed but cannot be opened. What blocks and why is `bygning/blocking.ts`.
 */
export function BlockingList({
  items,
  onOpenType,
  id,
  headingRef,
  cap,
  expanded = false,
  onExpandedChange,
  activeTypeId,
  phoneBar,
  failed,
  onRetry,
}: BlockingListProps) {
  const ownId = useId();
  const headingId = id ?? `${ownId}-blokerer`;
  const capped = cap !== undefined && items.length > cap;
  const shown = capped && !expanded ? items.slice(0, cap) : items;
  const listId = `${headingId}-list`;
  return (
    <section className={`${styles.section} ${phoneBar ? styles.bar : ''}`} aria-labelledby={headingId}>
      <div className={styles.head}>
        <h3 id={headingId} ref={headingRef} tabIndex={-1} className={styles.heading}>
          Det, der blokerer
        </h3>
        {items.length > 0 ? <Pill tone="warn">{blockingCountText(items.length)}</Pill> : <Pill tone="good">Intet</Pill>}
      </div>
      {failed && (
        <p className={`${styles.note} ${styles.barHidden}`} role="status">
          Fraktionerne kunne ikke hentes — listen bygger på typerne.{' '}
          {onRetry && (
            <TextLink size="sm" onClick={onRetry}>
              Prøv igen
            </TextLink>
          )}
        </p>
      )}
      {items.length === 0 ? (
        <p className={styles.note}>Intet blokerer.</p>
      ) : (
        <ul id={listId} className={styles.list}>
          {shown.map((item) => {
            const detail = blockingDetail(item);
            const held = item.partCount !== null;
            const active = activeTypeId === item.typeId;
            return (
              <li key={item.typeId}>
                <button
                  type="button"
                  className={`${styles.row} ${styles.plain} ${active ? styles.active : ''}`}
                  aria-pressed={activeTypeId === undefined ? undefined : active}
                  disabled={!held}
                  title={held ? undefined : 'Typen findes ikke i sagen endnu'}
                  onClick={() => onOpenType(item.typeId)}
                >
                  <span className={styles.text}>
                    <span className={styles.title}>{item.name}</span>
                    {detail && <small className={styles.detail}>{detail}</small>}
                  </span>
                  <span className={styles.pills}>
                    {item.reasons.map((r) => (
                      <Pill key={r} tone={BLOCK_REASON[r].tone}>
                        {BLOCK_REASON[r].text}
                      </Pill>
                    ))}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {capped && onExpandedChange && (
        <TextLink
          size="sm"
          className={styles.more}
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => onExpandedChange(!expanded)}
        >
          {expanded ? 'Vis færre' : `Vis alle ${items.length}`}
        </TextLink>
      )}
    </section>
  );
}
